import { useState, useEffect, useCallback } from 'react';
import { useAuth, GMAIL_ENABLED } from '@/contexts/AuthContext';
import { settingsApi, authApi, calendarApi } from '@/utils/api';
import { relTime } from '@/utils/time';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { countryName, REGION_LABELS } from '@/data/countries';
import { User, Mail, Bell, Shield, Loader2, CheckCircle2, ExternalLink, Send, CalendarClock, Unlink, AlertTriangle, Monitor, LogOut, Download, Trash2, Copy, RefreshCw, CalendarPlus } from 'lucide-react';

// Python weekday convention: Mon=0 … Sun=6 (matches backend digest_day)
const DIGEST_DAYS = [
  { v: 6, label: 'Sunday' }, { v: 0, label: 'Monday' }, { v: 1, label: 'Tuesday' },
  { v: 2, label: 'Wednesday' }, { v: 3, label: 'Thursday' }, { v: 4, label: 'Friday' },
  { v: 5, label: 'Saturday' },
];
const DIGEST_HOURS = Array.from({ length: 24 }, (_, h) => ({
  v: h,
  label: `${((h % 12) || 12)}:00 ${h < 12 ? 'AM' : 'PM'}`,
}));
const TIMEZONES = [
  'America/Los_Angeles', 'America/Denver', 'America/Chicago', 'America/New_York',
  'America/Sao_Paulo', 'Europe/London', 'Europe/Paris', 'Europe/Berlin',
  'Europe/Athens', 'Africa/Johannesburg', 'Asia/Dubai', 'Asia/Kolkata',
  'Asia/Singapore', 'Asia/Shanghai', 'Asia/Tokyo', 'Australia/Sydney', 'Pacific/Auckland',
];
const TZ_LABELS = {
  'America/Los_Angeles': 'Pacific (Los Angeles)', 'America/Denver': 'Mountain (Denver)',
  'America/Chicago': 'Central (Chicago)', 'America/New_York': 'Eastern (New York)',
  'America/Sao_Paulo': 'São Paulo', 'Europe/London': 'London', 'Europe/Paris': 'Paris',
  'Europe/Berlin': 'Berlin', 'Europe/Athens': 'Athens', 'Africa/Johannesburg': 'Johannesburg',
  'Asia/Dubai': 'Dubai', 'Asia/Kolkata': 'India (Kolkata)', 'Asia/Singapore': 'Singapore',
  'Asia/Shanghai': 'Shanghai', 'Asia/Tokyo': 'Tokyo', 'Australia/Sydney': 'Sydney',
  'Pacific/Auckland': 'Auckland',
};

export default function SettingsPage() {
  const { user, updateUser, connectGoogle, connectMicrosoft, logout } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [sendingPreview, setSendingPreview] = useState(false);
  const [sendingAllPreviews, setSendingAllPreviews] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: '', email: '', company: '', role: '' });
  const [sessions, setSessions] = useState(null);
  const [signingOutAll, setSigningOutAll] = useState(false);
  const [revokingJti, setRevokingJti] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [calSub, setCalSub] = useState(null);
  const [calLoading, setCalLoading] = useState(false);
  const [calResetting, setCalResetting] = useState(false);

  const handleExportData = async () => {
    setExporting(true);
    try {
      const res = await authApi.exportData();
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `milk-data-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Your data export has been downloaded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to export your data');
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await authApi.deleteAccount(deleteConfirm.trim());
      toast.success('Your account has been permanently deleted');
      await logout();
      window.location.href = '/';
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete your account');
      setDeleting(false);
    }
  };

  const loadSessions = useCallback(async () => {
    try {
      const res = await authApi.listSessions();
      setSessions(res.data.sessions || []);
    } catch {
      setSessions([]);
    }
  }, []);

  const revokeSession = async (jti) => {
    setRevokingJti(jti);
    try {
      await authApi.revokeSession(jti);
      setSessions((prev) => (prev || []).filter((s) => s.jti !== jti));
      toast.success('Signed out that device');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to sign out device');
    } finally {
      setRevokingJti(null);
    }
  };

  const signOutEverywhere = async () => {
    if (!window.confirm('Sign out of all other devices? Your current session stays active.')) return;
    setSigningOutAll(true);
    try {
      const res = await authApi.revokeAllSessions(true);
      setSessions((prev) => (prev || []).filter((s) => s.current));
      toast.success(`Signed out ${res.data.revoked} other ${res.data.revoked === 1 ? 'device' : 'devices'}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to sign out other devices');
    } finally {
      setSigningOutAll(false);
    }
  };

  const disconnectAccount = async (provider) => {
    if (!window.confirm(`Disconnect your ${provider === 'microsoft' ? 'Microsoft' : 'Google'} account? Milk will stop sending emails and scheduling on your behalf until you reconnect.`)) return;
    setDisconnecting(true);
    try {
      const res = await authApi.disconnect(provider);
      updateUser({ google_connected: res.data.google_connected, microsoft_connected: res.data.microsoft_connected });
      toast.success('Account disconnected');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to disconnect');
    } finally {
      setDisconnecting(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const res = await settingsApi.get();
      setSettings(res.data);
      setProfileForm({
        name: res.data.profile?.name || '',
        email: res.data.profile?.email || '',
        company: res.data.profile?.company || '',
        role: res.data.profile?.role || '',
      });
    } catch {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSessions(); }, [loadSessions]);

  useEffect(() => {
    let active = true;
    setCalLoading(true);
    calendarApi.getSubscription()
      .then((r) => { if (active) setCalSub(r.data); })
      .catch(() => {})
      .finally(() => { if (active) setCalLoading(false); });
    return () => { active = false; };
  }, []);

  const copyFeed = async () => {
    if (!calSub?.feed_url) return;
    try { await navigator.clipboard.writeText(calSub.feed_url); toast.success('Calendar link copied'); }
    catch { toast.error('Could not copy — select the link and copy manually'); }
  };

  const resetFeed = async () => {
    setCalResetting(true);
    try {
      const r = await calendarApi.resetSubscription();
      setCalSub(r.data);
      toast.success('Calendar link reset — the old link no longer works');
    } catch { toast.error('Could not reset the link'); }
    finally { setCalResetting(false); }
  };

  // Persist the auto-detected browser timezone once if the user hasn't set one,
  // so the scheduler uses their real timezone instead of the EST default.
  useEffect(() => {
    if (settings && !settings.profile?.timezone) {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (tz) {
          settingsApi.update({ profile: { timezone: tz } }).catch(() => {});
          setSettings(p => ({ ...p, profile: { ...p.profile, timezone: tz } }));
        }
      } catch { /* ignore */ }
    }
  }, [settings]);

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const res = await settingsApi.update({ profile: profileForm });
      setSettings(res.data);
      updateUser({ name: profileForm.name });
      toast.success('Profile saved!');
    } catch {
      toast.error('Failed to save profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const toggleNotification = async (key) => {
    const updated = { ...settings.notifications, [key]: !settings.notifications[key] };
    try {
      await settingsApi.update({ notifications: updated });
      setSettings(p => ({ ...p, notifications: updated }));
      toast.success('Updated', { duration: 1500 });
    } catch {
      toast.error('Failed to update');
    }
  };

  const sendDigestPreview = async () => {
    setSendingPreview(true);
    try {
      const res = await settingsApi.sendDigestPreview();
      toast.success(`Digest preview sent to ${res.data.to || user?.email || 'your inbox'}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send digest preview');
    } finally {
      setSendingPreview(false);
    }
  };

  const sendAllEmailPreviews = async () => {
    setSendingAllPreviews(true);
    try {
      const res = await settingsApi.sendAllEmailPreviews();
      toast.success(`Sent ${res.data.count} sample email${res.data.count !== 1 ? 's' : ''} to ${res.data.to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send email previews');
    } finally {
      setSendingAllPreviews(false);
    }
  };

  // Update one or more digest scheduling fields (digest_day / digest_hour) on notifications.
  const updateDigestSchedule = async (patch) => {
    const updated = { ...settings.notifications, ...patch };
    setSettings(p => ({ ...p, notifications: updated }));
    try {
      await settingsApi.update({ notifications: updated });
      toast.success('Digest schedule updated', { duration: 1500 });
    } catch {
      toast.error('Failed to update schedule');
      load();
    }
  };

  const updateTimezone = async (tz) => {
    setSettings(p => ({ ...p, profile: { ...p.profile, timezone: tz } }));
    try {
      await settingsApi.update({ profile: { timezone: tz } });
      toast.success('Timezone updated', { duration: 1500 });
    } catch {
      toast.error('Failed to update timezone');
      load();
    }
  };

  const browserTz = (() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return 'America/New_York'; }
  })();
  const currentTz = settings?.profile?.timezone || browserTz;
  const tzOptions = TIMEZONES.includes(currentTz) ? TIMEZONES : [currentTz, ...TIMEZONES];
  const digestDay = settings?.notifications?.digest_day ?? 6;
  const digestHour = settings?.notifications?.digest_hour ?? 18;
  const digestFrequency = settings?.notifications?.digest_frequency ?? 'weekly';

  const Field = ({ label, children }) => (
    <div className="space-y-1">
      <label className="block text-xs font-medium leading-none text-muted-foreground">{label}</label>
      {children}
    </div>
  );

  return (
    <div className="pagewrap" style={{ maxWidth: '720px' }}>
      <div className="page-head">
        <div>
          <h1 className="pt">Settings</h1>
          <p className="ps">Account &amp; preferences</p>
        </div>
      </div>

      <div className="space-y-4">

        {/* Profile */}
        <Card data-testid="settings-profile-form" className="bg-white border-border shadow-sm">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <User size={14} style={{ color: '#6994c6' }} /> Profile
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-3">
            {loading ? (
              <div className="space-y-3">{[1,2,3,4].map(i => <Skeleton key={i} className="h-11 w-full rounded-xl" />)}</div>
            ) : (
              <>
                {/* Single column on mobile, 2-col on sm+ */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Full name">
                    <input
                      value={profileForm.name}
                      onChange={e => setProfileForm(p => ({ ...p, name: e.target.value }))}
                      placeholder="Your name"
                      // 16px font prevents iOS auto-zoom
                      style={{ fontSize: '16px' }}
                      className="w-full h-11 px-3.5 rounded-xl border border-border bg-white outline-none focus:border-primary/50 transition-colors"
                    />
                  </Field>
                  <Field label="Email">
                    <input
                      value={profileForm.email}
                      disabled
                      style={{ fontSize: '16px' }}
                      className="w-full h-11 px-3.5 rounded-xl border border-border bg-white opacity-50 outline-none"
                    />
                  </Field>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Company">
                    <input
                      value={profileForm.company}
                      onChange={e => setProfileForm(p => ({ ...p, company: e.target.value }))}
                      placeholder="Your company"
                      style={{ fontSize: '16px' }}
                      className="w-full h-11 px-3.5 rounded-xl border border-border bg-white outline-none focus:border-primary/50 transition-colors"
                    />
                  </Field>
                  <Field label="Role">
                    <input
                      value={profileForm.role}
                      onChange={e => setProfileForm(p => ({ ...p, role: e.target.value }))}
                      placeholder="Contractor, Agent…"
                      style={{ fontSize: '16px' }}
                      className="w-full h-11 px-3.5 rounded-xl border border-border bg-white outline-none focus:border-primary/50 transition-colors"
                    />
                  </Field>
                </div>
                {/* Full-width save button on mobile */}
                <button
                  onClick={saveProfile}
                  disabled={savingProfile}
                  className="w-full sm:w-auto h-11 px-5 rounded-xl text-sm font-semibold text-white transition-all active:scale-[0.97] disabled:opacity-60"
                  style={{ background: '#6994c6' }}
                >
                  {savingProfile
                    ? <><Loader2 size={14} className="inline mr-1.5 animate-spin" />Saving…</>
                    : 'Save Profile'}
                </button>
              </>
            )}
          </CardContent>
        </Card>

        {/* Email & Calendar Integration */}
        <Card className="bg-white border-border shadow-sm">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Mail size={14} style={{ color: '#6994c6' }} /> {user?.microsoft_connected && !user?.google_connected ? 'Microsoft Account' : user?.google_connected ? 'Google Account' : 'Connected Account'}
            </CardTitle>
          </CardHeader>
          <CardContent data-testid="settings-email-integration-button" className="px-4 pb-4">
            {(user?.google_connected || user?.microsoft_connected) ? (() => {
              const activeProvider = user?.microsoft_connected && !user?.google_connected ? 'microsoft' : 'google';
              const health = user?.connection_health?.[activeProvider] || 'healthy';
              const needsReconnect = health === 'needs_reconnect';
              const servicesConnected = activeProvider === 'microsoft'
                ? user?.microsoft_services_connected : user?.google_services_connected;
              const connectFn = activeProvider === 'microsoft' ? connectMicrosoft : connectGoogle;
              // What Milk can do with the connected account (Google Gmail is gated by CASA).
              const canSendEmail = activeProvider === 'microsoft' || GMAIL_ENABLED;
              const serviceLabel = activeProvider === 'microsoft'
                ? 'Outlook & Calendar'
                : (GMAIL_ENABLED ? 'Gmail & Calendar' : 'Calendar');
              const connectedDesc = canSendEmail
                ? (activeProvider === 'microsoft'
                    ? 'Sends follow-ups from your Outlook & schedules to your Microsoft Calendar'
                    : 'Sends follow-ups from your Gmail & schedules to your Calendar')
                : 'Schedules events to your Google Calendar';
              return (
              <div className={`flex items-center justify-between p-3.5 rounded-xl border gap-3 ${needsReconnect ? 'border-amber-300 bg-amber-50/60' : 'border-border'}`} data-testid="settings-connection-health" data-health={health}>
                <div className="min-w-0 pr-1">
                  <p className="text-sm font-medium text-foreground truncate">{user?.email || 'Account'}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {needsReconnect
                      ? 'Your connection expired — follow-ups and scheduling will fail until you reconnect.'
                      : !servicesConnected
                        ? `Connect ${serviceLabel} to let Milk ${canSendEmail ? 'send follow-ups and ' : ''}schedule events on your behalf.`
                        : connectedDesc}
                  </p>
                  {!needsReconnect && servicesConnected && user?.connection_verified_at?.[activeProvider] && (
                    <p className="text-[11px] text-muted-foreground/80 mt-1 flex items-center gap-1" data-testid="connection-last-verified">
                      <CheckCircle2 size={11} className="text-green-500" />
                      Connection verified {relTime(user.connection_verified_at[activeProvider])}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {needsReconnect ? (
                    <button
                      onClick={() => connectFn('/settings')}
                      data-testid="settings-reconnect-health-button"
                      className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 h-8 rounded-lg text-white transition-colors"
                      style={{ background: '#d4a44a' }}
                    >
                      <AlertTriangle size={13} />Reconnect
                    </button>
                  ) : !servicesConnected ? (
                    <button
                      onClick={() => connectFn('/settings')}
                      data-testid="settings-connect-services-button"
                      className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 h-8 rounded-lg text-white transition-colors"
                      style={{ background: '#6994c6' }}
                    >
                      <Mail size={13} />Connect {serviceLabel}
                    </button>
                  ) : (
                    <span className="flex items-center gap-1.5 text-xs text-green-500 font-medium">
                      <CheckCircle2 size={14} />Connected
                    </span>
                  )}
                  <button
                    onClick={() => disconnectAccount(activeProvider)}
                    disabled={disconnecting}
                    data-testid="settings-disconnect-account-button"
                    className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 h-8 rounded-lg border transition-colors disabled:opacity-60"
                    style={{ color: '#B4332E', borderColor: 'rgba(180,51,46,0.3)' }}
                  >
                    {disconnecting ? <Loader2 size={13} className="animate-spin" /> : <Unlink size={13} />}Disconnect
                  </button>
                </div>
              </div>
              );
            })() : (
              <div className="p-3.5 rounded-xl border border-border" data-testid="settings-account-disconnected">
                <p className="text-sm font-medium text-foreground">No account connected</p>
                <p className="text-xs text-muted-foreground mt-0.5 mb-3">
                  Connect an account to let Milk send follow-ups on your behalf. Calendar syncing works without connecting — just subscribe below.
                </p>
                <div className="flex flex-wrap gap-2">
                  {GMAIL_ENABLED && (
                    <button
                      onClick={() => connectGoogle('/settings')}
                      data-testid="settings-reconnect-google-button"
                      className="inline-flex items-center gap-1.5 text-xs font-medium px-3 h-9 rounded-lg border transition-colors hover:bg-secondary"
                      style={{ color: '#6994c6', borderColor: 'rgba(105,148,198,0.3)' }}
                    >
                      <Mail size={13} />Connect Google
                    </button>
                  )}
                  <button
                    onClick={() => connectMicrosoft('/settings')}
                    data-testid="settings-reconnect-microsoft-button"
                    className="inline-flex items-center gap-1.5 text-xs font-medium px-3 h-9 rounded-lg border transition-colors hover:bg-secondary"
                    style={{ color: '#6994c6', borderColor: 'rgba(105,148,198,0.3)' }}
                  >
                    <Mail size={13} />Connect Microsoft
                  </button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Calendar subscription (no-OAuth) */}
        <Card className="bg-white border-border shadow-sm" data-testid="settings-calendar-subscription">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <CalendarPlus size={14} style={{ color: '#6994c6' }} /> Subscribe to your Milk calendar
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-3">
            <p className="text-xs text-muted-foreground">
              Add this link to Google, Outlook or Apple Calendar and your Milk follow-ups
              and task due dates appear automatically — no account connection needed.
              Subscribed calendars refresh on your provider's schedule (Google can take a few hours).
            </p>
            {calLoading ? (
              <Skeleton className="h-9 w-full rounded-lg" />
            ) : calSub?.feed_url ? (
              <>
                <div className="flex items-center gap-2">
                  <Input
                    readOnly
                    value={calSub.feed_url}
                    data-testid="calendar-feed-url"
                    onFocus={(e) => e.target.select()}
                    className="bg-secondary/40 border-border h-9 text-xs font-mono"
                  />
                  <button
                    onClick={copyFeed}
                    data-testid="calendar-copy-feed-button"
                    className="inline-flex items-center gap-1.5 text-xs font-medium px-3 h-9 rounded-lg border shrink-0 transition-colors hover:bg-secondary"
                    style={{ color: '#6994c6', borderColor: 'rgba(105,148,198,0.3)' }}
                  >
                    <Copy size={13} />Copy
                  </button>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <a
                    href={calSub.webcal_url}
                    data-testid="calendar-subscribe-link"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 h-9 rounded-lg text-white transition-colors"
                    style={{ background: '#6994c6' }}
                  >
                    <CalendarPlus size={13} />Subscribe now
                  </a>
                  <a
                    href={`https://calendar.google.com/calendar/r/settings/addbyurl?cid=${encodeURIComponent(calSub.feed_url)}`}
                    target="_blank" rel="noopener noreferrer"
                    data-testid="calendar-google-add-link"
                    className="inline-flex items-center gap-1.5 text-xs font-medium px-3 h-9 rounded-lg border transition-colors hover:bg-secondary"
                    style={{ color: '#6994c6', borderColor: 'rgba(105,148,198,0.3)' }}
                  >
                    <ExternalLink size={13} />Add to Google Calendar
                  </a>
                  <button
                    onClick={resetFeed}
                    disabled={calResetting}
                    data-testid="calendar-reset-feed-button"
                    className="inline-flex items-center gap-1.5 text-xs font-medium px-3 h-9 rounded-lg border border-red-200 text-red-600 transition-colors hover:bg-red-50 disabled:opacity-60"
                  >
                    {calResetting ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                    Reset link
                  </button>
                </div>
                <p className="text-[11px] text-muted-foreground/80">
                  Anyone with this link can see your reminder titles, so keep it private. Reset it anytime to revoke access.
                </p>
              </>
            ) : (
              <p className="text-sm text-muted-foreground py-1" data-testid="calendar-feed-error">Couldn't load your calendar link. Refresh to try again.</p>
            )}
          </CardContent>
        </Card>

        {/* Active Sessions */}
        <Card className="bg-white border-border shadow-sm" data-testid="settings-active-sessions">
          <CardHeader className="pb-2 pt-4 px-4">
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Monitor size={14} style={{ color: '#6994c6' }} /> Active sessions
              </CardTitle>
              {sessions && sessions.some((s) => !s.current) && (
                <button
                  onClick={signOutEverywhere}
                  disabled={signingOutAll}
                  data-testid="sign-out-everywhere-button"
                  className="inline-flex items-center gap-1.5 text-xs font-medium px-3 h-8 rounded-lg border border-red-200 text-red-600 transition-colors hover:bg-red-50 disabled:opacity-60"
                >
                  {signingOutAll ? <Loader2 size={13} className="animate-spin" /> : <LogOut size={13} />}
                  Sign out everywhere
                </button>
              )}
            </div>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <p className="text-xs text-muted-foreground mb-3">Devices currently signed in to your account.</p>
            {sessions === null ? (
              <div className="space-y-2">{[1, 2].map((i) => <Skeleton key={i} className="h-[52px] w-full rounded-xl" />)}</div>
            ) : sessions.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2" data-testid="sessions-empty">No active sessions found.</p>
            ) : (
              <div className="space-y-2">
                {sessions.map((s) => (
                  <div
                    key={s.jti}
                    data-testid="session-row"
                    className="flex items-center justify-between gap-3 p-3 rounded-xl border border-border"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-foreground truncate">{s.device}</span>
                        {s.current && (
                          <Badge data-testid="session-current-badge" className="text-[10px] px-1.5 py-0 h-4" style={{ background: 'rgba(105,148,198,0.12)', color: '#6994c6' }}>
                            This device
                          </Badge>
                        )}
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-0.5 truncate">
                        {s.ip || 'Unknown IP'} · signed in {relTime(s.created_at)}
                      </p>
                    </div>
                    {!s.current && (
                      <button
                        onClick={() => revokeSession(s.jti)}
                        disabled={revokingJti === s.jti}
                        data-testid={`revoke-session-${s.jti}`}
                        className="inline-flex items-center gap-1.5 text-xs font-medium px-3 h-8 rounded-lg border border-border transition-colors hover:bg-secondary disabled:opacity-60 shrink-0"
                      >
                        {revokingJti === s.jti ? <Loader2 size={12} className="animate-spin" /> : <LogOut size={12} />}
                        Sign out
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Notifications */}
        <Card className="bg-white border-border shadow-sm">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Bell size={14} style={{ color: '#6994c6' }} /> Notifications
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-2">
            {loading ? (
              <div className="space-y-3 pb-2">{[1,2,3].map(i => <Skeleton key={i} className="h-[52px] w-full rounded-xl" />)}</div>
            ) : (
              <>
                {[
                  { key: 'task_due', label: 'Tasks due today', desc: 'Daily reminder for due tasks' },
                  { key: 'overdue', label: 'Overdue tasks', desc: 'Alert when tasks are past due' },
                  { key: 'new_action', label: 'Action Card ready', desc: 'When AI finishes processing' },
                  { key: 'weekly_digest', label: 'Email digest', desc: digestFrequency === 'daily' ? 'Daily recap: overdue, today & follow-ups' : 'Weekly recap: overdue, this week & follow-ups' },
                ].map(({ key, label, desc }) => (
                  <div key={key} className="flex items-center justify-between min-h-[52px] py-2 border-b border-border/40 last:border-0">
                    <div className="pr-4">
                      <p className="text-sm font-medium text-foreground">{label}</p>
                      <p className="text-xs text-muted-foreground">{desc}</p>
                    </div>
                    <span className="inline-flex shrink-0">
                      <Switch
                        data-testid={`settings-notifications-switch-${key}`}
                        checked={settings?.notifications?.[key] ?? true}
                        onCheckedChange={() => toggleNotification(key)}
                      />
                    </span>
                  </div>
                ))}
                {(settings?.notifications?.weekly_digest ?? true) && (
                  <div className="pt-3 pb-1 space-y-3">
                    <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                      <CalendarClock size={13} style={{ color: '#6994c6' }} />
                      When should your digest land?
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                      <Select value={digestFrequency} onValueChange={(v) => updateDigestSchedule({ digest_frequency: v })}>
                        <SelectTrigger data-testid="digest-frequency-select" className="h-10 rounded-xl bg-white text-sm">
                          <SelectValue placeholder="Frequency" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="weekly" data-testid="digest-frequency-option-weekly">Weekly</SelectItem>
                          <SelectItem value="daily" data-testid="digest-frequency-option-daily">Daily</SelectItem>
                        </SelectContent>
                      </Select>
                      {digestFrequency !== 'daily' && (
                        <Select value={String(digestDay)} onValueChange={(v) => updateDigestSchedule({ digest_day: Number(v) })}>
                          <SelectTrigger data-testid="digest-day-select" className="h-10 rounded-xl bg-white text-sm">
                            <SelectValue placeholder="Day" />
                          </SelectTrigger>
                          <SelectContent>
                            {DIGEST_DAYS.map(d => (
                              <SelectItem key={d.v} value={String(d.v)} data-testid={`digest-day-option-${d.v}`}>{d.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      <Select value={String(digestHour)} onValueChange={(v) => updateDigestSchedule({ digest_hour: Number(v) })}>
                        <SelectTrigger data-testid="digest-hour-select" className="h-10 rounded-xl bg-white text-sm">
                          <SelectValue placeholder="Time" />
                        </SelectTrigger>
                        <SelectContent className="max-h-64">
                          {DIGEST_HOURS.map(h => (
                            <SelectItem key={h.v} value={String(h.v)} data-testid={`digest-hour-option-${h.v}`}>{h.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select value={currentTz} onValueChange={updateTimezone}>
                        <SelectTrigger data-testid="digest-timezone-select" className="h-10 rounded-xl bg-white text-sm">
                          <SelectValue placeholder="Timezone" />
                        </SelectTrigger>
                        <SelectContent className="max-h-64">
                          {tzOptions.map(tz => (
                            <SelectItem key={tz} value={tz} data-testid={`digest-tz-option-${tz}`}>{TZ_LABELS[tz] || tz}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <button
                      data-testid="settings-send-digest-preview-button"
                      onClick={sendDigestPreview}
                      disabled={sendingPreview}
                      className="inline-flex items-center gap-2 text-xs font-medium h-9 px-3.5 rounded-lg border transition-colors active:scale-[0.97] disabled:opacity-60"
                      style={{ color: '#6994c6', borderColor: 'rgba(105,148,198,0.3)', background: 'rgba(105,148,198,0.04)' }}
                    >
                      {sendingPreview
                        ? <><Loader2 size={13} className="animate-spin" />Sending…</>
                        : <><Send size={13} />Send me a preview now</>}
                    </button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Email templates */}
        <Card className="bg-white border-border shadow-sm">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Mail size={14} style={{ color: '#6994c6' }} /> Email templates
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <p className="text-xs text-muted-foreground mb-3">
              Send yourself one sample of every Milk email (branded notification, recap, weekly digest) to QA the branding.
            </p>
            <button
              data-testid="settings-send-all-email-previews-button"
              onClick={sendAllEmailPreviews}
              disabled={sendingAllPreviews}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 text-sm font-medium h-11 px-5 rounded-xl border transition-colors active:scale-[0.97] disabled:opacity-60"
              style={{ color: '#6994c6', borderColor: 'rgba(105,148,198,0.3)', background: 'rgba(105,148,198,0.04)' }}
            >
              {sendingAllPreviews
                ? <><Loader2 size={14} className="animate-spin" />Sending samples…</>
                : <><Send size={14} />Preview all email types</>}
            </button>
          </CardContent>
        </Card>

        {/* AI Preferences */}
        <Card className="bg-white border-border shadow-sm">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Shield size={14} style={{ color: '#6994c6' }} /> AI Preferences
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <p className="text-xs text-muted-foreground mb-3">
              Personalise how milk generates summaries, tasks, and emails.
            </p>
            <button
              onClick={() => window.location.href = '/onboarding?edit=1'}
              className="w-full flex items-center justify-center gap-2 text-sm font-medium h-11 rounded-xl border transition-colors active:scale-[0.97]"
              style={{ color: '#6994c6', borderColor: 'rgba(105,148,198,0.3)', background: 'rgba(105,148,198,0.04)' }}
            >
              <ExternalLink size={14} />
              Reconfigure AI preferences
            </button>
          </CardContent>
        </Card>

        {/* Privacy & your data (PIPEDA) */}
        <Card className="bg-white border-border shadow-sm" data-testid="privacy-data-card">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Shield size={14} style={{ color: '#6994c6' }} /> Privacy &amp; your data
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-4">
            {user?.home_region && (
              <div className="rounded-xl p-3" data-testid="settings-data-residency"
                style={{ background: 'rgba(105,148,198,0.06)', border: '1px solid rgba(105,148,198,0.2)' }}>
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="text-xs font-semibold text-foreground">Data residency</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {countryName(user.country)} — stored in {REGION_LABELS[user.home_region] || user.home_region}
                    </p>
                  </div>
                  <Badge variant="secondary" className="text-[10px] shrink-0">Locked</Badge>
                </div>
                <p className="text-[11px] text-muted-foreground mt-2">
                  Set once at signup and cannot be changed. Contact support to request a region change.
                </p>
              </div>
            )}
            <div>
              <p className="text-xs text-muted-foreground mb-3">
                Download a complete copy of your personal data — your profile, Action Cards, tasks, contacts,
                drafts, meetings and files metadata — as a JSON file (PIPEDA access right).
              </p>
              <button
                data-testid="settings-export-data-button"
                onClick={handleExportData}
                disabled={exporting}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 text-sm font-medium h-11 px-5 rounded-xl border transition-colors active:scale-[0.97] disabled:opacity-60"
                style={{ color: '#6994c6', borderColor: 'rgba(105,148,198,0.3)', background: 'rgba(105,148,198,0.04)' }}
              >
                {exporting
                  ? <><Loader2 size={14} className="animate-spin" />Preparing export…</>
                  : <><Download size={14} />Download my data</>}
              </button>
            </div>

            <div className="pt-4 border-t border-border">
              <div className="flex items-start gap-2 mb-3">
                <AlertTriangle size={15} className="text-red-500 mt-0.5 shrink-0" />
                <p className="text-xs text-muted-foreground">
                  <span className="font-semibold text-red-600">Delete my account.</span> Permanently erase your
                  account and all associated data. This cannot be undone (PIPEDA right to erasure).
                </p>
              </div>
              <button
                data-testid="settings-delete-account-button"
                onClick={() => { setDeleteConfirm(''); setDeleteOpen(true); }}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 text-sm font-medium h-11 px-5 rounded-xl border border-red-200 text-red-600 bg-red-50 hover:bg-red-100 transition-colors active:scale-[0.97]"
              >
                <Trash2 size={14} />Delete my account
              </button>
            </div>
          </CardContent>
        </Card>

      </div>

      <Dialog open={deleteOpen} onOpenChange={(o) => { if (!deleting) setDeleteOpen(o); }}>
        <DialogContent data-testid="delete-account-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle size={18} /> Delete your account
            </DialogTitle>
            <DialogDescription>
              This permanently deletes your account and <strong>all</strong> your data — Action Cards, tasks,
              contacts, drafts, meetings and files. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Type your email <span className="font-semibold text-foreground">{user?.email}</span> to confirm:
            </p>
            <Input
              data-testid="delete-account-confirm-input"
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              placeholder={user?.email}
              autoComplete="off"
            />
          </div>
          <DialogFooter>
            <button
              onClick={() => setDeleteOpen(false)}
              disabled={deleting}
              className="inline-flex items-center justify-center text-sm font-medium h-10 px-4 rounded-xl border border-border bg-white hover:bg-secondary/40 transition-colors disabled:opacity-60"
              data-testid="delete-account-cancel-button"
            >
              Cancel
            </button>
            <button
              data-testid="delete-account-confirm-button"
              onClick={handleDeleteAccount}
              disabled={deleting || deleteConfirm.trim().toLowerCase() !== (user?.email || '').toLowerCase()}
              className="inline-flex items-center justify-center gap-2 text-sm font-medium h-10 px-4 rounded-xl bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              {deleting
                ? <><Loader2 size={14} className="animate-spin" />Deleting…</>
                : <><Trash2 size={14} />Permanently delete</>}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
